/**
 * SearchableSelect — typeahead-filtered combobox using shadcn primitives.
 *
 * Drop-in replacement for native <select>. Filters options by label as user types.
 *
 * Usage:
 *   <SearchableSelect
 *     value={planCode}
 *     onChange={setPlanCode}
 *     options={plans.map(p => ({ value: p.code, label: `${p.name} · ₹${p.price_monthly_inr}/mo`, sub: p.code }))}
 *     placeholder="Select a plan"
 *     testId="plan-select"
 *   />
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

export function SearchableSelect({
    value, onChange, options = [], placeholder = "Select…", emptyMessage = "No results",
    testId, disabled = false, allowClear = false, className = "",
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    const selectedOpt = useMemo(
        () => options.find(o => o.value === value),
        [options, value],
    );

    const filtered = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter(o =>
            (o.label || "").toLowerCase().includes(q) ||
            (o.sub || "").toLowerCase().includes(q) ||
            (o.value || "").toLowerCase().includes(q),
        );
    }, [options, query]);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        if (open) {
            document.addEventListener("mousedown", onClick);
            setTimeout(() => inputRef.current?.focus(), 0);
            return () => document.removeEventListener("mousedown", onClick);
        }
    }, [open]);

    const pick = (opt) => {
        onChange?.(opt.value);
        setOpen(false);
        setQuery("");
    };

    return (
        <div ref={wrapperRef} className={`relative ${className}`} data-testid={testId}>
            <button type="button" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)}
                data-testid={testId ? `${testId}-trigger` : undefined}
                className={`w-full inline-flex items-center justify-between gap-2 px-3 py-2 bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm rounded-lg text-foreground ${
                    disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary cursor-pointer"
                }`}>
                <span className={`flex-1 text-left truncate ${selectedOpt ? "text-foreground" : "text-muted-foreground"}`}>
                    {selectedOpt ? selectedOpt.label : placeholder}
                </span>
                <span className="flex items-center gap-1">
                    {allowClear && selectedOpt && !disabled && (
                        <span onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                              role="button" tabIndex={0} aria-label="Clear selection"
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onChange?.(""); } }}
                              data-testid={testId ? `${testId}-clear` : undefined}
                              className="hover:text-rose-500 cursor-pointer"><X className="w-3.5 h-3.5"/></span>
                    )}
                    <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground"/>
                </span>
            </button>

            {open && (
                <div data-testid={testId ? `${testId}-dropdown` : undefined}
                    className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                    <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                        type="text" placeholder="Type to search…"
                        data-testid={testId ? `${testId}-search` : undefined}
                        className="w-full px-3 py-2 border-b border-border bg-background text-sm text-foreground outline-none"/>
                    <div className="overflow-y-auto flex-1">
                        {filtered.length === 0 ? (
                            <div className="p-3 text-center text-xs text-muted-foreground" data-testid={testId ? `${testId}-empty` : undefined}>{emptyMessage}</div>
                        ) : (
                            filtered.map(opt => (
                                <button key={opt.value} type="button" onClick={() => pick(opt)}
                                    data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-primary/5 ${
                                        opt.value === value ? "bg-primary/10 text-primary" : "text-foreground"
                                    }`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate">{opt.label}</div>
                                        {opt.sub && <div className="text-[10px] text-muted-foreground font-mono truncate">{opt.sub}</div>}
                                    </div>
                                    {opt.value === value && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0"/>}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SearchableSelect;
